































import {
  defineComponent,
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  provide,
  ref,
  set,
  watch,
} from "@vue/composition-api";
import { differenceInMinutes } from "date-fns";
import { throttle } from "lodash-es";
import { CallOptions } from "@scrile/api-provider/dist/api/CallOptionsProvider";
import { isLikeNotificationDetails } from "@scrile/api-provider/dist/api/LikeProvider";
import { InputMessageSearch, isMessageSubjectDetails, Message } from "@scrile/api-provider/dist/api/MessagesProvider";
import { MessageThread } from "@scrile/api-provider/dist/api/MessageThreadsProvider";
import { Notification } from "@scrile/api-provider/dist/api/NotificationsProvider";
import { PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { FragmentFields } from "@scrile/api-provider/dist/api/types";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { useAiUserSettings } from "@/hooks/useAiUserSettings";
import useAppointments from "@/hooks/useAppointments";
import useAuthUser from "@/hooks/useAuthUser";
import usePager from "@/hooks/usePager";
import usePaymentPackages from "@/hooks/usePaymentPackages";
import useRole from "@/hooks/useRole";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import config from "@/config";
import { EVENTS, PROVIDED_DATA, PROVIDED_DATA_KEY } from "@/const";
import MessagesView from "./components/MessagesView.vue";
import useStartAppointment from "./hooks/useStartAppointment";

export default defineComponent({
  name: "PageMessages",
  components: { MessagesView },
  props: {
    userId: { type: String, required: true },
  },
  setup(props, { root }) {
    const { authUserStrict, isCreator } = useAuthUser();
    const { paymentPackages, getPaymentPackages } = usePaymentPackages([PaymentPackageEnum.MESSAGE_ATTACHMENT]);
    const { aiAllowed, aiResponseEnabled, aiMode, updateAiUserSettings, getAiUserSettings } = useAiUserSettings();
    const { isBookingAvailable, checkBookingAvailable } = useAppointments();
    const { nearestAppointment, isAppointmentStart, isMessagingAppointmentStart } = useStartAppointment(
      computed(() => props.userId)
    );

    const loading = ref(false);
    const processingPin = ref(false);
    const allowResponse = ref(true);
    const showAiBanner = ref(false);
    const checkConvertingStatusTimeoutId = ref<ReturnType<typeof setTimeout> | null>(null);
    const opponentPingInterval = ref<ReturnType<typeof setInterval> | null>(null);
    const messageThread = ref<MessageThread>();
    const opponent = ref<UserPublicData>();
    const userCallOptions = ref<CallOptions>();
    const messagesViewRef = ref();
    const isSubscribedToOpponent = ref(false);

    const providedData = ref<PROVIDED_DATA[PROVIDED_DATA_KEY.OPPONENT_USER]>({ user: null });

    const { isCreator: isOpponentCreator, isMember: isOpponentMember } = useRole(opponent);

    const {
      enableScroll,
      data: messagesSearchResult,
      items,
      getNextPage: getPrevMessages,
      getFirstPage: getMessages,
      removeItem,
      updateItem,
      addItem,
    } = usePager<Message, InputMessageSearch>(async (page, requestData) => {
      return providers.MessagesProvider.find({
        data: {
          threadId: messageThread.value?.id,
          sort: "TIME,DESC",
          page,
          ...requestData,
        },
      });
    });
    const messageList = computed(() => Array.from(items.value).reverse());

    const convertingMessages = computed<Message[]>(() => {
      return messageList.value.filter(
        msg =>
          msg.attachment.length > 0 && msg.attachment.some(f => ["NEW", "CONVERTING"].includes(f.file.processingStatus))
      );
    });

    const minUnreadMessageId = computed(() => messageThread.value?.minUnreadMessageId ?? "0");
    const opponentMinUnreadMessageId = computed(() => messageThread.value?.opponentMinUnreadMessageId ?? "0");
    const lastMessageTime = computed(() => {
      return messagesSearchResult.value?.result[0]?.time;
    });

    const togglePin = async () => {
      if (!messageThread.value || processingPin.value) return;
      try {
        processingPin.value = true;
        const pinned = await providers.MessageThreadsProvider.togglePin({ threadId: messageThread.value.id });
        emitter.emit(EVENTS.NOTIFICATION_SHOW, {
          text: pinned ? $t("Marked as important") : $t("Marked as common"),
          timeout: 2000,
        });
        set(messageThread.value, "pinTime", pinned ? new Date().toISOString() : null);
      } finally {
        processingPin.value = false;
      }
    };

    const cleanTimeout = () => {
      checkConvertingStatusTimeoutId.value && clearTimeout(checkConvertingStatusTimeoutId.value);
      checkConvertingStatusTimeoutId.value = null;
    };
    const checkConvertingStatus = () => {
      cleanTimeout();

      checkConvertingStatusTimeoutId.value = setTimeout(async () => {
        const ids = (convertingMessages.value ?? []).map(i => i.id);

        if (ids.length === 0) {
          cleanTimeout();
          return;
        }
        const updatedMessages = await providers.MessagesProvider.find({ data: { ids } });
        if (updatedMessages.result.length > 0 && messagesSearchResult.value) {
          updatedMessages.result.forEach(m => updateItem(m, (i1, i2) => i1.id === i2.id));
        }
        checkConvertingStatus();
      }, 3000);
    };

    const getThread = async () => {
      try {
        providers.MessagesProvider.setReceiveErrors(false);
        messageThread.value = await providers.MessageThreadsProvider.findPersonal({ opponentId: props.userId });
        opponent.value = messageThread.value.users.find(i => i.id === props.userId);
      } catch (e) {}
    };

    const toggleHideThread = async () => {
      if (!messageThread.value) return;
      messageThread.value = await providers.MessageThreadsProvider.hide({ id: messageThread.value.id });
    };
    const getOpponentUserInfo = async () => {
      const resp = await providers.UserPublicDataProvider.search({ data: { id: [props.userId] } });
      opponent.value = resp.result[0];
    };

    const getCallOptions = async () => {
      if (!config.featureCalling) return;
      userCallOptions.value = (await providers.CallOptionsProvider.find({ userIds: [props.userId] })).find(
        i => i.userId === props.userId
      );
    };

    const getMessageSettings = async (userId: string) => {
      const settings = await providers.MessageSettingsProvider.search({ userIds: [userId], size: 1 });
      allowResponse.value = settings.result.find(i => i.userId === userId)?.allowIncoming ?? true;
    };

    const markedRead = (message: Message) => {
      if (!messageThread.value) return;
      let id = Number(message.id);
      const minimumId = Number(minUnreadMessageId.value);
      if (minimumId > 0 || minimumId < id) {
        const lastMessage = messageList.value[messageList.value.length - 1];
        if (lastMessage.id === message.id) id = -1;
        set(messageThread.value, "minUnreadMessageId", id + 1 + "");
      }
    };

    const addMessage = async (message: Message) => {
      if (messageList.value.find(m => m.id === message.id)) {
        return;
      }
      if (!messageThread.value || !messagesSearchResult.value) {
        await getThread();
        return getMessages();
      } else {
        set(messageThread.value, "hidden", false);
        if (messageThread.value?.opponentMinUnreadMessageId === "0") {
          messageThread.value = { ...messageThread.value, opponentMinUnreadMessageId: message.id };
        }
        addItem(message);
      }
    };

    const receivedMessage = async (message?: Message) => {
      if (!message) return;
      if (message.senderId === "0" && !messageThread.value) {
        await getThread();
        await getMessages();
      }
      if (messageThread.value?.id !== message.threadId || messageList.value.find(m => m.id === message.id)) {
        return;
      }
      await addMessage(message);
      if (message.senderId !== authUserStrict.value.id) {
        if (message.senderUser) {
          set(opponent.value, "onlineStatus", message.senderUser.onlineStatus);
        }
        if (messageThread.value && messageThread.value.minUnreadMessageId === "0") {
          set(messageThread.value, "minUnreadMessageId", message.id);
        }
      }
      await nextTick();
      messagesViewRef.value?.receivedMessage();
    };

    const updatePurchasedMessage = async (messageId: string) => {
      if (!messagesSearchResult.value) return;
      const response = await providers.MessagesProvider.find({ data: { ids: [messageId] } });
      const updatedMessage = response.result[0];
      updateItem(updatedMessage, (i1, i2) => i1.id === i2.id);
    };

    const removeMessage = (messageId: string) => {
      removeItem(i => i.id === messageId);
      if (messagesSearchResult.value?.result.length === 0) {
        messageThread.value = undefined;
        messagesSearchResult.value = undefined;
      }
    };

    const checkActiveSubscriptions = async () => {
      if (isOpponentCreator.value) {
        const res = await providers.SubscriptionProvider.search({
          data: {
            consumerIds: [authUserStrict.value.id],
            providerId: props.userId,
            expired: false,
          },
        });
        isSubscribedToOpponent.value = res.totalRecords > 0;
      }
    };

    const checkAppointments = async () => {
      if (!opponent.value || !isOpponentCreator.value || isCreator.value) return;
      await checkBookingAvailable(props.userId, isSubscribedToOpponent.value);
    };

    const updateThread = async (thread: MessageThread) => {
      if (thread.id === messageThread.value?.id) messageThread.value = thread;
    };

    const updateLikedMessage = async (payload: { notification?: Notification; messageId?: string }) => {
      let updatedMessage;
      if (
        !!payload.notification &&
        isLikeNotificationDetails(payload.notification.details) &&
        isMessageSubjectDetails(payload.notification.details.content)
      ) {
        updatedMessage = payload.notification.details.content.message;
      }
      if (!!payload.messageId) {
        const response = await providers.MessagesProvider.find({ data: { ids: [payload.messageId] } });
        updatedMessage = response.result[0];
      }
      if (!!updatedMessage) {
        updateItem(updatedMessage, (i1, i2) => i1.id === i2.id);
      }
    };

    const pingOpponent = async () => {
      if (!opponent.value || !lastMessageTime.value) return;
      const fragment: FragmentFields<UserPublicData> = [
        "id",
        { onlineStatus: ["userId", "lastOnlineTime", "isOnline"] },
      ];
      if (differenceInMinutes(new Date(Date.now()), new Date(lastMessageTime.value)) > 1) {
        const response = await providers.UserPublicDataProvider.search({ data: { id: [opponent.value.id] } }, fragment);
        if (!!response.result.length) {
          set(opponent.value, "onlineStatus", response.result[0].onlineStatus);
        }
      }
    };

    const handleUpdateAiResponse = async ({ aiResponseEnabled }: { aiResponseEnabled: boolean }, cb?: () => void) => {
      try {
        await updateAiUserSettings({ aiResponseEnabled });
      } finally {
        cb && cb();
      }
    };

    const getAiSettings = async () => {
      await getAiUserSettings();
      if (aiAllowed.value) {
        const userQuestionaryResponse = await providers.AiUserQuestionaryProvider.search({
          userId: authUserStrict.value.id,
        });
        showAiBanner.value = !(
          userQuestionaryResponse?.fields && Object.values(userQuestionaryResponse.fields).some(value => !!value)
        );
      }
    };

    const startOpponentPing = () => {
      opponentPingInterval.value = setInterval(() => pingOpponent(), 60000);
    };

    const stopOpponentPing = () => {
      opponentPingInterval.value && clearInterval(opponentPingInterval.value);
    };

    const getData = async () => {
      try {
        loading.value = true;
        if (props.userId === authUserStrict.value.id) {
          root.$router.push({ name: "messageThreads" });
          return;
        }
        providers.MessageThreadsProvider.setReceiveErrors(false);
        const promises: Promise<any>[] = [
          getCallOptions(),
          getMessageSettings(props.userId),
          getPaymentPackages(),
          checkActiveSubscriptions(),
        ];
        if (!messageThread.value) {
          await getThread();
        }
        if (!opponent.value) {
          await getOpponentUserInfo();
        }
        if (!opponent.value) {
          root.$router.push({ name: "messageThreads" });
          return;
        }
        if (messageThread.value) {
          promises.push(getMessages());
        }
        if (config.featureAIMessages && isOpponentMember.value) {
          promises.push(getAiSettings());
        }
        await Promise.all(promises);
        await checkAppointments();
      } catch (e) {
        console.error(e);
      } finally {
        loading.value = false;
      }
    };

    const markRead = throttle(
      (message: Message) => {
        emitter.emit(EVENTS.MESSAGES_MARK_READ, message);
      },
      1000,
      { leading: false }
    );

    provide<PROVIDED_DATA[PROVIDED_DATA_KEY.OPPONENT_USER]>(PROVIDED_DATA_KEY.OPPONENT_USER, providedData.value);

    watch(opponent, () => {
      providedData.value.user = opponent.value ? opponent.value : null;
    });

    watch(
      messagesSearchResult,
      () => {
        const ids = (convertingMessages.value ?? []).map(i => i.id);
        if (ids.length > 0 && !checkConvertingStatusTimeoutId.value) {
          checkConvertingStatus();
        }
      },
      { deep: true }
    );

    watch(
      () => props.userId,
      () => {
        loading.value = false;
        enableScroll.value = false;
        messagesSearchResult.value = undefined;
        messageThread.value = undefined;
        opponent.value = undefined;
        userCallOptions.value = undefined;
        getData();
      }
    );

    getData();
    startOpponentPing();
    emitter.on<Message>(EVENTS.MESSAGES_NEW_MESSAGE, receivedMessage);
    emitter.on<any>(EVENTS.MESSAGES_MARKED_READ, markedRead);
    emitter.on<any>(EVENTS.MESSAGES_UPDATE_MESSAGE, updatePurchasedMessage);
    emitter.on<any>(EVENTS.MESSAGES_UPDATE_THREAD, updateThread);
    emitter.on<any>(EVENTS.MESSAGES_LIKED_MESSAGE, updateLikedMessage);

    onMounted(() => {
      emitter.emit(EVENTS.MAIN_MENU_HIDE, true);
    });

    onBeforeUnmount(() => {
      emitter.off<Message>(EVENTS.MESSAGES_NEW_MESSAGE, receivedMessage);
      emitter.off<any>(EVENTS.MESSAGES_MARKED_READ, markedRead);
      emitter.off<any>(EVENTS.MESSAGES_UPDATE_MESSAGE, updatePurchasedMessage);
      emitter.off<any>(EVENTS.MESSAGES_UPDATE_THREAD, updateThread);
      emitter.off<any>(EVENTS.MESSAGES_LIKED_MESSAGE, updateLikedMessage);
      emitter.emit(EVENTS.MAIN_MENU_HIDE, false);
      cleanTimeout();
      stopOpponentPing();
    });

    return {
      messagesViewRef,
      loading,
      authUserStrict,
      messageThread,
      messageList,
      minUnreadMessageId,
      opponentMinUnreadMessageId,
      opponent,
      userCallOptions,
      enableScroll,
      paymentPackages,
      allowResponse,
      nearestAppointment,
      isAppointmentStart,
      isMessagingAppointmentStart,
      isBookingAvailable,
      aiAllowed,
      aiResponseEnabled,
      showAiBanner,
      aiMode,
      handleUpdateAiResponse,
      togglePin,
      markRead,
      removeMessage,
      getPrevMessages,
      addMessage,
      toggleHideThread,
    };
  },
});
